import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { sync } from '@ces/sync';
import { CanDeactivateGuard } from '@egovsolutions/angular-can-deactivate-guard';
import { PaymentType } from '@egovsolutions/angular-payment-types';
import { Store } from '@ngrx/store';
import { NewPaymentMethodFormComponent } from '@payment/components/new-payment-method-form/new-payment-method-form.component';
import { PaymentService } from '@payment/services/payment.service';
import { loadPaymentState } from '@payment/state/payment.actions';
import { selectAvailablePaymentTypes } from '@payment/state/payment.selectors';
import { SimpleErrorHandling } from '@shared/services/simple-error-handling.service';
import { State } from '@state/model';
import { EgovAPIError } from 'egov-api';
import { BehaviorSubject } from 'rxjs';

@Component({
    selector: 'app-new-payment-method',
    template: `
        <egov-dialog-close-button [closeHandler]="closeAttempt"></egov-dialog-close-button>
        <div class="new-payment-method-dialog">
            <form (ngSubmit)="submit()" *ngIf="availablePaymentTypes$ | async; else loadingPlaceholder">
                <egov-new-payment-method-form [availablePaymentTypes]="availablePaymentTypes$ | async"></egov-new-payment-method-form>
                <div class="actions">
                    <button
                        mat-flat-button
                        class="default spinner-button"
                        type="submit"
                        [disabled]="(enabled$ | async) !== true"
                        [class.submitting]="(enabled$ | async) === false"
                    >
                        <span class="label">Add Payment Method</span>
                        <app-spinner-button-spinner *ngIf="(enabled$ | async) === false"></app-spinner-button-spinner>
                    </button>
                </div>
            </form>
            <ng-template #loadingPlaceholder>
                <app-progress-bar class="app-progress-bar"></app-progress-bar>
                <app-content-placeholder type="loading" customMessage="Loading payment service. Please wait..." icon="credit_card"></app-content-placeholder>
            </ng-template>
        </div>
    `,
    styleUrls: ['./new-payment-method.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewPaymentMethodComponent
{
    constructor
    (

        // Dependencies

        private readonly service: PaymentService,
        private readonly canDeactivateGuard: CanDeactivateGuard,
        private readonly simpleErrorHandling: SimpleErrorHandling,
        private readonly store: Store<State>,


        // Dialog reference

        private readonly dialogRef: MatDialogRef<NewPaymentMethodComponent, string>,
    )
    {
        this.store.dispatch(loadPaymentState('PaymentComponent'));
    }


    // Reference to form component

    @ViewChild(NewPaymentMethodFormComponent, { static: false }) protected formComponent!: NewPaymentMethodFormComponent;


    // Deactivate prevention

    protected closeAttempt: () => void = this.canDeactivateGuard.addDialog(this.dialogRef, () =>
    {
        const currentControls = sync(this.formComponent.currentControls$);

        for (const name in currentControls)
            if ((currentControls as any)[name].dirty)
                return 'Discard new payment method?';

        return false;
    });


    // State

    protected readonly enabled$ = new BehaviorSubject(true);

    private disable()
    {
        this.enabled$.next(false);
        this.formComponent.disable();
    }

    private enable()
    {
        this.enabled$.next(true);
        this.formComponent.enable();
    }

    protected readonly availablePaymentTypes$ = this.store.select(selectAvailablePaymentTypes);


    // Submit

    protected submit()
    {
        this.disable();

        const formValue = sync(this.formComponent.value$);
        const selectedPaymentOption = this.formComponent.selectedPaymentOption$.value;

        switch (selectedPaymentOption)
        {
            case PaymentType.USCard:
            case PaymentType.NonUSCard:

                this.service.addCreditCard(
                    selectedPaymentOption === PaymentType.USCard,
                    formValue.name,
                    formValue.cardNumber,
                    this.formComponent.cardType$.value,
                    formValue.expiration.split('/')[0],
                    formValue.expiration.split('/')[1],
                    formValue.cv2,
                    formValue.address,
                    formValue.zip
                )
                .subscribe({
                    next: newCard => this.dialogRef.close(newCard.id),
                    error: error =>
                    {
                        this.displayError(error);
                        this.enable();
                    }
                });

                break;

            case PaymentType.ECheck:

                this.service.addECheck(
                    formValue.name,
                    formValue.accountNumber,
                    formValue.routingNumber,
                    formValue.address,
                    formValue.zip
                )
                .subscribe({
                    next: newEcheck => this.dialogRef.close(newEcheck.id),
                    error: error =>
                    {
                        this.displayError(error);
                        this.enable();
                    }
                });

                break;
        }
    }

    private displayError(error: EgovAPIError)
    {
        this.simpleErrorHandling.displayBestErrorMessage(error, {
            customMessage: 'Error adding payment method.',
            retryHandler: () => this.submit()
        });
    }
}
